import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import ModulePage from "./pages/ModulePage";
import AdminPage from "./pages/AdminPage";
import { Toaster } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Error Boundary to prevent blank screen on DOM errors
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Ignore removeChild DOM errors - they're caused by external scripts
        if (error instanceof DOMException && error.name === "NotFoundError") {
            this.setState({ hasError: false });
            return;
        }
        console.error("ErrorBoundary caught:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-white flex items-center justify-center">
                    <div className="text-center space-y-4">
                        <p className="text-slate-600">Ocurrió un error inesperado</p>
                        <button
                            onClick={() => {
                                this.setState({ hasError: false });
                                window.location.href = "/";
                            }}
                            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                        >
                            Volver al Inicio
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-enterprise-bg flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-400">Cargando...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Public Route Component (redirects if logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-enterprise-bg flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-400">Cargando...</p>
                </div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Seed database on first load
const SeedInitializer = ({ children }) => {
    useEffect(() => {
        const seedDatabase = async () => {
            try {
                await axios.post(`${API}/seed`);
                console.log("Database seeded successfully");
            } catch (error) {
                // Database might already be seeded, that's okay
                console.log("Database seed check completed");
            }
        };
        seedDatabase();
    }, []);

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/modulo/:slug/*"
                element={
                    <ProtectedRoute>
                        <ModulePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <ErrorBoundary>
                <BrowserRouter>
                    <AuthProvider>
                        <SeedInitializer>
                            <ErrorBoundary>
                                <AppRoutes />
                            </ErrorBoundary>
                            <Toaster richColors position="top-right" />
                        </SeedInitializer>
                    </AuthProvider>
                </BrowserRouter>
            </ErrorBoundary>
        </div>
    );
}

export default App;
